import { PROFILE, SKILLS, EXPERIENCE, EDUCATION } from "@/data/portfolio";
import { journeyUptime } from "@/hooks/useNow";

export const COMMANDS = [
  ["help", "list every command"],
  ["who is he", "who is Anish — the short version"],
  ["whoami", "alias — same as `who is he`"],
  ["about", "the about-me file"],
  ["skills", "the full skills tree"],
  ["experience", "work history, condensed"],
  ["education", "degrees & timeline"],
  ["neofetch", "the machine & the setup"],
  ["contact", "email · phone · linkedin · github"],
  ["resume", "download the PDF resume"],
  ["clock", "live IST clock + time-coding counter"],
  ["ls", "list files in this workspace"],
  ["cat <file>", "print a file"],
  ["open <app>", "launch an app window"],
  ["clear", "clear the terminal"],
  ["echo <text>", "say it back"],
  ["date", "current date & time"],
  ["exit", "close the terminal"],
];

const FILES = {
  "about.txt": "about",
  "skills.txt": "skills",
  "experience.log": "experience",
  "education.md": "education",
  "contact.sh": "contact",
  "achievements.txt": "EMPTY",
};

const NEOFETCH_ART = [
  "    ▄▄▄▄▄▄▄▄    ",
  "  ▄██████████▄  ",
  " ███      ███   ",
  " ███  ▄▄  ███   ",
  " ███      ███   ",
  "  ▀██████████▀  ",
  "    ▀▀▀▀▀▀▀▀    ",
];

const OPEN_MAP = {
  about: "about",
  skills: "skills",
  experience: "experience",
  education: "education",
  devenv: "devenv",
  contact: "contact",
  resume: "resume",
  terminal: "terminal",
  home: "readme",
  readme: "readme",
};

export function execute(raw, ctx) {
  const trimmed = (raw || "").trim();
  const parts = trimmed.split(/\s+/);
  const cmd = (parts.shift() || "").toLowerCase();
  const arg = parts.join(" ");
  const out = [];
  const add = (text, cls = "text-slate-300") => out.push({ text, cls });
  let action = null;

  switch (cmd) {
    case "":
      return { lines: out };

    case "help": {
      add("available commands:", "text-sky-300");
      COMMANDS.forEach(([c, d]) => add(`  ${c.padEnd(14)} ${d}`, "text-slate-400"));
      add("tip: ↑ walks your history · ctrl+l clears", "text-slate-500");
      return { lines: out };
    }

    case "whoami":
    case "who is he":
    case "who": {
      if (ctx.name) add(`not you, ${ctx.name} — me:`, "text-slate-500");
      add("Anish Ravi — Software Development Engineer", "text-white");
      add("Bengaluru, India · building banking & payment infrastructure");
      add("Nearly 2 years shipping Java 17 / Spring Boot microservices that");
      add("clear 2M+ transactions a day — with Kafka, Oracle SQL and a");
      add("healthy obsession for reconciliation and latency.");
      add("type `about` for more · `contact` to reach me", "text-slate-500");
      add("opening about.txt …", "text-slate-500");
      action = () => ctx.open("about");
      return { lines: out, action };
    }

    case "about": {
      add(PROFILE.summary);
      add("");
      add("currently: SDE I @ Hatio Innovations (A BillDesk Company), Kochi", "text-sky-300");
      add("home base: Bengaluru, India", "text-sky-300");
      add("opening about.txt …", "text-slate-500");
      action = () => ctx.open("about");
      return { lines: out, action };
    }

    case "skills": {
      add("skills/", "text-white");
      SKILLS.forEach((g, i) => {
        add(`${i === SKILLS.length - 1 ? "└──" : "├──"} ${g.group}/`, "text-sky-300");
        add(`│   ${g.items.join(" · ")}`);
      });
      add("`open skills` for the pretty version", "text-slate-500");
      action = () => ctx.open("skills");
      return { lines: out, action };
    }

    case "experience":
    case "work": {
      EXPERIENCE.forEach((j) => {
        add(`${j.role} — ${j.company}`, "text-white");
        add(`${j.period} · ${j.location}`, "text-slate-500");
        j.highlights.slice(0, 3).forEach((h) => add(`  • ${h}`));
        if (j.highlights.length > 3) add(`  … +${j.highlights.length - 3} more in the window`, "text-slate-500");
        add("");
      });
      action = () => ctx.open("experience");
      return { lines: out, action };
    }

    case "education": {
      EDUCATION.forEach((e) => {
        add(`${e.degree}`, "text-white");
        add(`${e.institution} · ${e.location}`, "text-slate-400");
        add(`${e.period}`, "text-sky-300");
        add("");
      });
      action = () => ctx.open("education");
      return { lines: out, action };
    }

    case "neofetch": {
      const info = [
        "anish@portfolio",
        "───────────────",
        "host     MacBook Air M4",
        "os       macOS + Ubuntu Linux",
        "shell    zsh",
        "vcs      Git · GitHub",
        "ship     Docker · CI/CD",
        "code     Java 17 · React",
        "uptime   since Sep 2024",
      ];
      const rows = NEOFETCH_ART.map((a, i) => `${a}  ${info[i] || ""}`);
      rows.forEach((r, i) => add(r, i < NEOFETCH_ART.length ? "text-sonoma" : "text-sky-300"));
      add("");
      add("opening dev environment …", "text-slate-500");
      action = () => ctx.open("devenv");
      return { lines: out, action };
    }

    case "contact": {
      add(`email     ${PROFILE.email}`, "text-sky-300");
      add(`linkedin  ${PROFILE.linkedinLabel}`, "text-sky-300");
      add(`github    ${PROFILE.githubLabel}`, "text-sky-300");
      add("tip: `open contact` has a form that emails me directly", "text-slate-500");
      add("opening contact.sh …", "text-slate-500");
      action = () => ctx.open("contact");
      return { lines: out, action };
    }

    case "resume": {
      add("fetching AnishResume_WA.pdf …", "text-slate-400");
      add("download started — thanks for reading!", "text-green-400");
      action = () => {
        ctx.download();
        ctx.open("resume");
      };
      return { lines: out, action };
    }

    case "clock":
    case "time":
    case "uptime": {
      const now = new Date();
      add(
        now.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata", weekday: "long", day: "numeric", month: "long", year: "numeric" }),
        "text-slate-400"
      );
      add(`${now.toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour12: false })} IST`, "text-white");
      add(`${journeyUptime(now)} in the code mines since Sep 2024`, "text-green-400");
      return { lines: out };
    }

    case "ls": {
      add(Object.keys(FILES).join("   "), "text-sky-300");
      add("AnishResume_WA.pdf", "text-emerald-300");
      return { lines: out };
    }

    case "cat": {
      const f = arg.toLowerCase();
      if (!f) {
        add("usage: cat <file> — try `ls` first", "text-amber-300");
      } else if (f === "achievements.txt") {
        add("achievements.txt — 0 bytes (empty)", "text-slate-400");
        add("no awards section in the résumé — the work history speaks for itself.", "text-slate-500");
      } else if (FILES[f]) {
        const res = execute(FILES[f], ctx);
        return res;
      } else {
        add(`cat: ${arg}: No such file or directory`, "text-red-400");
        add("hint: run `ls` to see what's here", "text-slate-500");
      }
      return { lines: out };
    }

    case "open": {
      const target = OPEN_MAP[arg.toLowerCase()];
      if (target) {
        add(`opening ${arg.toLowerCase()} …`, "text-slate-400");
        action = () => ctx.open(target);
      } else {
        add(`open: unknown app '${arg}'`, "text-red-400");
        add("try: about · skills · experience · education · devenv · contact · resume · terminal", "text-slate-500");
      }
      return { lines: out, action };
    }

    case "clear":
      return { lines: [], clear: true };

    case "sudo":
      add("anish is not in the sudoers file. This incident will be reported… to GitHub.", "text-red-400");
      return { lines: out };

    case "echo":
      add(arg || "");
      return { lines: out };

    case "date":
      add(new Date().toString(), "text-slate-400");
      return { lines: out };

    case "greet": {
      if (ctx.name) {
        add(`hello again, ${ctx.name}.`, "text-green-400");
      } else {
        add("hello, guest! I'm Anish — what should I call you?", "text-green-400");
        add("type it in the popup (or click `say hi` up top) and I'll remember for this visit.", "text-slate-500");
        action = () => ctx.askName();
      }
      return { lines: out, action };
    }

    case "exit": {
      add("closing session — see you around.", "text-slate-500");
      action = () => ctx.close("terminal");
      return { lines: out, action };
    }

    default:
      add(`zsh: command not found: ${cmd}`, "text-red-400");
      add("type `help` to see what I can do", "text-slate-500");
      return { lines: out };
  }
}
